<!--
  Settings → Network — inventaire du réseau de l'usine (caméras, NVR, équipements).

  Les adresses IP des caméras et des NVR avaient été perdues. Cette page les retrouve et les
  garde documentées. Le serveur de l'ERP n'atteint pas le LAN usine : seul le serveur XYLO VISION
  (EW1) a le tunnel WireGuard, donc le scan est relayé à EW1 (/api/network/scan → EW1). Le dernier
  inventaire est mis en cache côté ERP : la page reste utile même quand le lien VPN est coupé.

  Scan 100 % lecture seule : EW1 ouvre une connexion TCP, lit la bannière, referme. Aucun
  identifiant envoyé, rien de modifié sur les équipements.
-->
<template>
  <div id="network-devices">

    <!-- Hero / résumé -->
    <vx-card class="mb-base net-hero">
      <div class="flex flex-wrap items-start justify-between">
        <div class="hero-left">
          <h2 class="hero-title">Factory network</h2>
          <p class="hero-sub">
            Cameras, recorders (NVR) and network gear on <b>{{ inventory.network || '192.168.1.0/24' }}</b>.
          </p>
          <div class="hero-meta">
            <span class="meta-item">
              <feather-icon icon="ClockIcon" svgClasses="w-4 h-4" class="align-middle" />
              Last scan: <b>{{ scannedAtLabel }}</b>
            </span>
            <span v-if="inventory.scannedFrom" class="meta-item">
              <feather-icon icon="ShieldIcon" svgClasses="w-4 h-4" class="align-middle" />
              {{ inventory.scannedFrom }}
            </span>
          </div>
        </div>

        <div class="hero-right">
          <div class="stat-row">
            <div class="stat">
              <div class="stat-n">{{ count('camera') }}</div>
              <div class="stat-l">Cameras</div>
            </div>
            <div class="stat">
              <div class="stat-n">{{ count('nvr') }}</div>
              <div class="stat-l">Recorders</div>
            </div>
            <div class="stat">
              <div class="stat-n">{{ devices.length }}</div>
              <div class="stat-l">Devices</div>
            </div>
          </div>
          <vs-button
            class="mt-4 w-full"
            color="primary"
            icon-pack="feather"
            :icon="scanning ? 'icon-loader' : 'icon-radio'"
            :disabled="scanning"
            @click="rescan"
          >
            {{ scanning ? 'Scanning the network…' : 'Scan now' }}
          </vs-button>
        </div>
      </div>

      <!-- Note VPN : le scan passe par le tunnel du serveur XYLO VISION -->
      <div class="vpn-note">
        <feather-icon icon="InfoIcon" svgClasses="w-4 h-4" class="align-middle mr-1" />
        A scan reaches the factory LAN through the XYLO VISION server's VPN link (WireGuard tunnel).
        If that link is down, the scan fails and the last known list below is kept. The device web
        pages are only reachable from inside that tunnel, never from the public internet.
      </div>

      <div v-if="scanError" class="err-note">
        <feather-icon icon="AlertTriangleIcon" svgClasses="w-4 h-4" class="align-middle mr-1" />
        {{ scanError }}
      </div>
    </vx-card>

    <!-- Filtres -->
    <div class="flex flex-wrap items-center justify-between mb-4">
      <div class="chips">
        <button
          v-for="f in filters"
          :key="f.key"
          class="chip"
          :class="{ active: activeFilter === f.key }"
          @click="activeFilter = f.key"
        >
          {{ f.label }}
          <span class="chip-n">{{ f.count }}</span>
        </button>
      </div>
      <div class="search-wrap">
        <feather-icon icon="SearchIcon" svgClasses="w-4 h-4" class="search-ic" />
        <input v-model="search" class="search" type="text" placeholder="Search IP, model, name…" />
      </div>
    </div>

    <div v-if="loading" class="text-center py-12 text-muted">Loading inventory…</div>

    <div v-else-if="!devices.length" class="empty-state">
      <feather-icon icon="WifiOffIcon" svgClasses="w-8 h-8" class="mb-2" />
      <div class="font-medium">No device recorded yet</div>
      <div class="text-sm">Run a scan while the factory VPN link is up.</div>
    </div>

    <template v-else>
      <!-- Une section par groupe, avec les NVR mis en avant -->
      <div v-for="sec in visibleSections" :key="sec.key" class="section">
        <div class="section-head">
          <feather-icon :icon="sec.icon" svgClasses="w-5 h-5" class="align-middle mr-2" :class="sec.cls" />
          <h3 class="section-title">{{ sec.label }}</h3>
          <span class="section-n">{{ sec.items.length }}</span>
        </div>

        <div class="grid" :class="sec.key === 'nvr' ? 'grid-wide' : ''">
          <div
            v-for="d in sec.items"
            :key="d.ip"
            class="dev-card"
            :class="[sec.key, { ewann: d.isEwann, big: sec.key === 'nvr' }]"
          >
            <div class="dev-top">
              <feather-icon :icon="iconFor(d)" svgClasses="w-5 h-5" class="dev-ic" />
              <div class="dev-name">
                {{ d.name }}
                <span v-if="d.isEwann" class="tag tag-ewann">EW1</span>
              </div>
            </div>

            <div class="dev-ip" @click="copy(d.ip)" :title="'Copy ' + d.ip">
              {{ d.ip }}
              <feather-icon :icon="copied === d.ip ? 'CheckIcon' : 'CopyIcon'" svgClasses="w-4 h-4" class="copy-ic" />
            </div>

            <div v-if="d.model" class="dev-model">{{ d.model }}</div>
            <div v-if="d.note" class="dev-note">{{ d.note }}</div>

            <div class="dev-ports">
              <span v-if="d.rtsp" class="pill pill-rtsp">RTSP</span>
              <span v-if="d.https" class="pill pill-tls">HTTPS</span>
              <span v-for="p in webPorts(d)" :key="p" class="pill">:{{ p }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <p v-if="inventory.note" class="src-note mt-2">{{ inventory.note }}</p>
  </div>
</template>

<script>
import axios from '@/axios.js'

// Groupes d'affichage (ordre = ordre à l'écran). Les NVR d'abord : c'est ce qu'on avait perdu.
const SECTIONS = [
  { key: 'nvr',    label: 'Recorders (NVR)', icon: 'HardDriveIcon', cls: 'c-nvr',   cats: ['nvr'] },
  { key: 'camera', label: 'Cameras',         icon: 'VideoIcon',     cls: 'c-cam',   cats: ['camera'] },
  { key: 'infra',  label: 'Infrastructure',  icon: 'ServerIcon',    cls: 'c-infra', cats: ['router', 'switch', 'vpn'] },
  { key: 'pc',     label: 'Computers',       icon: 'MonitorIcon',   cls: 'c-pc',    cats: ['pc'] },
  { key: 'other',  label: 'Other devices',   icon: 'BoxIcon',       cls: 'c-other', cats: ['other', 'unknown'] }
]

export default {
  name: 'NetworkDevices',
  data () {
    return {
      loading: true,
      scanning: false,
      scanError: '',
      search: '',
      activeFilter: 'all',
      copied: '',
      inventory: { network: '', scannedFrom: '', scannedAt: null, note: '', summary: {}, devices: [] }
    }
  },
  computed: {
    devices () {
      return this.inventory.devices || []
    },
    scannedAtLabel () {
      if (!this.inventory.scannedAt) return 'never'
      const d = new Date(this.inventory.scannedAt)
      if (isNaN(d)) return String(this.inventory.scannedAt)
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    // Appareils filtrés par recherche puis répartis en sections.
    filtered () {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.devices
      return this.devices.filter(d =>
        (d.ip || '').toLowerCase().includes(q) ||
        (d.model || '').toLowerCase().includes(q) ||
        (d.name || '').toLowerCase().includes(q)
      )
    },
    sections () {
      return SECTIONS
        .map(s => ({ ...s, items: this.filtered.filter(d => s.cats.includes(d.category)) }))
        .filter(s => s.items.length)
    },
    visibleSections () {
      if (this.activeFilter === 'all') return this.sections
      if (this.activeFilter === 'infra') return this.sections.filter(s => s.key === 'infra' || s.key === 'pc')
      return this.sections.filter(s => s.key === this.activeFilter)
    },
    filters () {
      const n = (keys) => this.filtered.filter(d => keys.includes(d.category)).length
      return [
        { key: 'all',    label: 'All',            count: this.filtered.length },
        { key: 'camera', label: 'Cameras',        count: n(['camera']) },
        { key: 'nvr',    label: 'Recorders',      count: n(['nvr']) },
        { key: 'infra',  label: 'Infrastructure', count: n(['router', 'switch', 'vpn', 'pc']) },
        { key: 'other',  label: 'Other',          count: n(['other', 'unknown']) }
      ]
    }
  },
  created () {
    this.load()
  },
  methods: {
    count (cat) {
      return this.devices.filter(d => d.category === cat).length
    },
    iconFor (d) {
      if (d.category === 'camera') return 'VideoIcon'
      if (d.category === 'nvr') return 'HardDriveIcon'
      if (d.category === 'switch') return 'GitMergeIcon'
      if (d.category === 'vpn') return 'KeyIcon'
      if (d.category === 'router') return 'WifiIcon'
      if (d.category === 'pc') return 'MonitorIcon'
      return 'BoxIcon'
    },
    // Ports web lisibles (on masque le bruit : SDK 8000, SMB 445, RDP 3389…).
    webPorts (d) {
      const show = [80, 81, 443, 8080, 8081, 8443, 9000]
      return (d.ports || []).filter(p => show.includes(p))
    },
    load () {
      this.loading = true
      axios.get('/network/inventory')
        .then(res => { this.inventory = res.data || this.inventory })
        .catch(() => { this.scanError = 'Could not load the saved inventory.' })
        .finally(() => { this.loading = false })
    },
    rescan () {
      this.scanning = true
      this.scanError = ''
      axios.post('/network/scan', {}, { timeout: 95000 })
        .then(res => { this.inventory = res.data || this.inventory })
        .catch(err => {
          const d = (err.response && err.response.data) || {}
          this.scanError = d.message || 'The scan failed. Check the factory VPN link.'
          if (d.inventory) this.inventory = d.inventory
        })
        .finally(() => { this.scanning = false })
    },
    copy (ip) {
      const done = () => { this.copied = ip; setTimeout(() => { if (this.copied === ip) this.copied = '' }, 1500) }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(ip).then(done).catch(done)
      } else {
        const t = document.createElement('textarea'); t.value = ip; document.body.appendChild(t)
        t.select(); try { document.execCommand('copy') } catch (e) {} document.body.removeChild(t); done()
      }
    }
  }
}
</script>

<style scoped>
#network-devices { color: #1e293b; }
.text-muted { color: #6c757d; }
.src-note { font-size: 0.78rem; color: #94a3b8; }

/* Hero */
.net-hero >>> .vx-card__body { padding: 1.6rem 1.8rem; }
.net-hero { background: linear-gradient(135deg, #1e293b 0%, #334155 100%); border: none; }
.net-hero, .net-hero * { color: #e2e8f0; }
.hero-left { max-width: 540px; }
.hero-title { font-size: 1.5rem; font-weight: 700; color: #fff; margin: 0 0 4px; }
.hero-sub { color: #cbd5e1; margin-bottom: 12px; }
.hero-sub b { color: #fff; }
.hero-meta { display: flex; flex-wrap: wrap; gap: 18px; }
.meta-item { font-size: 0.8rem; color: #94a3b8; }
.meta-item b { color: #e2e8f0; font-weight: 600; }

.hero-right { min-width: 260px; }
.stat-row { display: flex; gap: 10px; }
.stat {
  flex: 1; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px; padding: 12px 8px; text-align: center;
}
.stat-n { font-size: 1.7rem; font-weight: 700; color: #fff; line-height: 1; }
.stat-l { font-size: 0.68rem; text-transform: uppercase; letter-spacing: .05em; color: #94a3b8; margin-top: 5px; }

.vpn-note {
  margin-top: 18px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px; padding: 10px 14px; font-size: 0.78rem; color: #cbd5e1; line-height: 1.5;
}
.err-note {
  margin-top: 12px; background: rgba(239,68,68,0.15); border: 1px solid rgba(239,68,68,0.4);
  border-radius: 8px; padding: 10px 14px; font-size: 0.82rem; color: #fecaca;
}

/* Filtres */
.chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
  border: 1px solid #e2e8f0; background: #fff; color: #475569; border-radius: 999px;
  padding: 6px 14px; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: all .15s;
}
.chip:hover { border-color: #cbd5e1; }
.chip.active { background: #7367f0; border-color: #7367f0; color: #fff; }
.chip-n {
  display: inline-block; margin-left: 6px; padding: 0 7px; border-radius: 999px;
  background: rgba(0,0,0,0.08); font-size: 0.7rem;
}
.chip.active .chip-n { background: rgba(255,255,255,0.25); }

.search-wrap { position: relative; }
.search-ic { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.search {
  border: 1px solid #dae1e7; border-radius: 8px; padding: 8px 12px 8px 32px;
  font-size: 0.85rem; color: #1e293b; background: #fff; width: 240px;
}

/* Sections */
.section { margin-bottom: 26px; }
.section-head { display: flex; align-items: center; margin-bottom: 12px; }
.section-title { font-size: 1.05rem; font-weight: 700; margin: 0; color: #1e293b; }
.section-n {
  margin-left: 10px; padding: 1px 9px; border-radius: 999px; background: #eef2ff;
  color: #4f46e5; font-size: 0.72rem; font-weight: 700;
}
.c-nvr { color: #d946ef; } .c-cam { color: #7367f0; } .c-infra { color: #0ea5e9; }
.c-pc { color: #64748b; } .c-other { color: #94a3b8; }

/* Grille de cartes */
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 12px; }
.grid-wide { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

.dev-card {
  background: #fff; border: 1px solid #e9eef5; border-radius: 12px; padding: 14px 16px;
  transition: box-shadow .15s, transform .15s, border-color .15s;
}
.dev-card:hover { box-shadow: 0 6px 18px rgba(30,41,59,0.08); transform: translateY(-1px); border-color: #dbe3ee; }
.dev-card.nvr { border-left: 3px solid #d946ef; }
.dev-card.camera { border-left: 3px solid #7367f0; }
.dev-card.ewann { border-left: 3px solid #10b981; background: #f6fefb; }
.dev-card.big { padding: 18px 20px; }

.dev-top { display: flex; align-items: center; margin-bottom: 8px; }
.dev-ic { color: #64748b; margin-right: 8px; flex-shrink: 0; }
.dev-name { font-weight: 600; font-size: 0.92rem; color: #1e293b; line-height: 1.2; }

.dev-ip {
  display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
  font-family: 'SFMono-Regular', Consolas, monospace; font-size: 1.05rem; font-weight: 700;
  color: #0f172a; margin: 2px 0 6px; padding: 2px 4px; border-radius: 5px;
}
.dev-ip:hover { background: #f1f5f9; }
.dev-card.big .dev-ip { font-size: 1.3rem; }
.copy-ic { color: #94a3b8; }

.dev-model {
  display: inline-block; font-family: 'SFMono-Regular', Consolas, monospace; font-size: 0.72rem;
  font-weight: 600; color: #4f46e5; background: #eef2ff; border-radius: 5px; padding: 2px 7px; margin-bottom: 6px;
}
.dev-note { font-size: 0.76rem; color: #64748b; line-height: 1.4; margin-bottom: 8px; }

.dev-ports { display: flex; flex-wrap: wrap; gap: 5px; }
.pill {
  font-size: 0.66rem; font-weight: 600; padding: 1px 7px; border-radius: 999px;
  background: #f1f5f9; color: #64748b; font-family: 'SFMono-Regular', Consolas, monospace;
}
.pill-rtsp { background: #ecfeff; color: #0e7490; }
.pill-tls { background: #ecfdf5; color: #047857; }

.tag { font-size: 0.62rem; font-weight: 700; padding: 1px 6px; border-radius: 4px; margin-left: 6px; vertical-align: middle; }
.tag-ewann { background: #d1fae5; color: #047857; }

.empty-state { text-align: center; color: #94a3b8; padding: 48px 10px; }

@media (max-width: 640px) {
  .hero-right { min-width: 0; width: 100%; margin-top: 16px; }
  .search { width: 100%; }
  .search-wrap { width: 100%; margin-top: 10px; }
}
</style>
